import { useState } from 'react';
import { AXIS_TEXT, NAVY } from './palette';

export interface ColunaDia {
  data: string;
  dia: number;
  horas: number;
  horasLabel: string;
  fimDeSemana: boolean;
  detalhe?: string;
}

interface Props {
  dias: ColunaDia[];
  /** Expected hours for a normal working day — drawn as a reference line. */
  alvo: number;
  alvoLabel: string;
  onSelect?: (data: string) => void;
}

/**
 * Change over time across the month: one thin column per day, weekends muted,
 * and a single dashed reference line for the expected daily total. One axis
 * only — hours. Tapping a column jumps to that day.
 */
export function ColunasDiarias({ dias, alvo, alvoLabel, onSelect }: Props) {
  const [hover, setHover] = useState<ColunaDia | null>(null);
  const max = Math.max(alvo, ...dias.map((d) => d.horas), 1);
  const alvoPct = (alvo / max) * 100;

  return (
    <div>
      <div className="relative h-36 md:h-48 lg:h-56">
        {/* Reference line for the expected daily total. */}
        {alvo > 0 && (
          <div
            className="pointer-events-none absolute inset-x-0 z-10 border-t border-dashed border-[#b9c0d2]"
            style={{ bottom: `${alvoPct}%` }}
          >
            <span className="num absolute -top-4 right-0 bg-white/85 px-1 text-[9px] text-[#7c8393]">
              {alvoLabel}
            </span>
          </div>
        )}

        <div className="flex h-full items-end gap-[2px]">
          {dias.map((d) => (
            <button
              key={d.data}
              type="button"
              onClick={() => onSelect?.(d.data)}
              onMouseEnter={() => setHover(d)}
              onMouseLeave={() => setHover(null)}
              onFocus={() => setHover(d)}
              onBlur={() => setHover(null)}
              aria-label={`${d.data}: ${d.horasLabel}`}
              className="group relative flex h-full flex-1 flex-col justify-end rounded-t-[4px]
                         focus:outline-none focus-visible:ring-2 focus-visible:ring-conduril/40"
            >
              <span
                className="w-full rounded-t-[4px] transition-[height] duration-300"
                style={{
                  height: `${d.horas > 0 ? Math.max(3, (d.horas / max) * 100) : 1.5}%`,
                  background: d.horas > 0 ? (d.fimDeSemana ? '#c3cade' : NAVY) : '#e9ebf2',
                }}
              />
            </button>
          ))}
        </div>
      </div>

      {/* Day-number axis, thinned so labels never collide on a phone. */}
      <div className="mt-1.5 flex gap-[2px]" style={{ color: AXIS_TEXT }}>
        {dias.map((d) => (
          <span key={d.data} className="num flex-1 text-center text-[8px] leading-none">
            {d.dia % 5 === 0 || d.dia === 1 ? d.dia : ''}
          </span>
        ))}
      </div>

      <div className="mt-2 h-8 text-[11px]">
        {hover ? (
          <div className="rounded-lg bg-conduril-tint px-2.5 py-1.5 text-conduril-ink">
            <span className="num font-semibold">{hover.horasLabel}</span>
            {hover.detalhe && <span className="ml-2 text-conduril-muted">{hover.detalhe}</span>}
          </div>
        ) : (
          <span className="text-conduril-muted">&nbsp;</span>
        )}
      </div>
    </div>
  );
}
